<script setup>
import { computed, defineProps, h, onMounted, ref, watch, onUnmounted } from "vue";
import { useStore } from "vuex";
import { AppButton, Checkbox, Icons, Modal, RadioButton, SimpleSelect } from "@n11/so-ui-components";
import { FlexRender, getCoreRowModel, useVueTable } from "@tanstack/vue-table";
import Pagination from "@/components/Table/Pagination";
import { DEAL_TYPES, OFFER_STATUS } from '@/utils/enum/offerStatus';
import { DEAL_LISTING_PAGE_SIZES } from "@/utils/enum/configs";
import {
  sendBulkOfferManagementClickEvent,
  sendCategoryCommissionButtonClickEvent,
  sendDealScreenViewEvent,
} from "@/utils/ga4/helper";
import {
  getAcceptedDealCount,
  revokeAllDeals,
  revokeSelectedDeals,
} from "../services/productDealService";

const store = useStore();
const pageConfig =
  store.getters["common/getConfigList"][DEAL_LISTING_PAGE_SIZES];

//config apisinden data gelmemesi durumu icin default pagesizes verildi
const defaultPageSizes = {
  10: "10 Ürün",
  25: "25 Ürün",
  50: "50 Ürün",
  100: "100 Ürün",
};

const emit = defineEmits(["goBulkDealManagement", "onPageChanged", "onFilterChanged"]);

const sellerInfo = computed(() => ({
  sellerId: store.state.auth.user.sellerId || '',
  sellerName: store.state.auth.user.username || '',
}));

const props = defineProps({
  isLoading: {
    type: Boolean,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
  data: {
    type: Array,
    required: true,
  },
  pagination: {
    type: Object,
    required: true,
  },
  pageCount: {
    type: Number,
    required: true,
  },
  pageChanged: {
    type: Function,
    required: true,
  },
});

const tableDealRef = ref(null);
const dealsTable = ref(null);
const activeFilter = ref('ALL');
const selectedDeals = ref([]);
const isRevokeLoading = ref(false);
const acceptedDealCount = ref(null);
const allRevokeScope = ref('CURRENT_PAGE');
const wasAllSelectedFromHeader = ref(false);
const manualSelectionChangedAfterSelectAll = ref(false);
const hasResolvedRevokeScope = ref(false);
const isAllAcceptedDealsSelected = ref(false);
const selectedRevokeModalRef = ref(null);
const allRevokeModalRef = ref(null);
const isTableRefreshing = ref(false);
// Header "select all" is provisional until the scope modal is confirmed.
// While pending, the bottom counter must keep showing the pre-selection count
// and any dismiss (Geri Dön / X / dışarı tıklama) must revert the selection.
const isScopeSelectionPending = ref(false);
const selectionSnapshotBeforeSelectAll = ref([]);

const TAB_NAMES = {
  ALL: 'Tüm Teklifleriniz',
  SPECIAL: 'Özel Teklifleriniz',
};

const handleGoBulkDealManagement = () => {
  sendBulkOfferManagementClickEvent({
    page_type: 'Ürün Komisyon Teklifleri',
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });
  emit('goBulkDealManagement');
};

const handleFilterChange = (type) => {
  if (type === 'SPECIAL') {
    sendCategoryCommissionButtonClickEvent({
      button_name: TAB_NAMES.SPECIAL,
      page_type: 'Ürün Komisyon Teklifleri',
      seller_name: sellerInfo.value.sellerName,
      seller_id: sellerInfo.value.sellerId,
    });
  }
  sendDealScreenViewEvent({
    page_type: 'Ürün Komisyon Teklifleri',
    tab: TAB_NAMES[type],
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });
  activeFilter.value = type;
  emit("onFilterChanged", type === 'SPECIAL' ? { isCustomDeal: true } : {});
}

onMounted(() => {
  sendDealScreenViewEvent({
    page_type: 'Ürün Komisyon Teklifleri',
    tab: TAB_NAMES.ALL,
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });
});

const visibleRows = computed(() => {
  return (dealsTable?.value.getRowModel().rows || []) ?? [];
});

const getDealKey = (deal) => {
  return `${deal.dealDto.dealId}-${deal.dealDto.groupId}`;
};

const isDealSelectable = (deal) => {
  if (deal.dealType === DEAL_TYPES.BO_DELETED_DEAL) return false;
  if (deal.acceptedDealDto) return true;

  return (
    deal.dealStatus !== OFFER_STATUS.WAITING_FOR_ACCEPTANCE &&
    deal.dealStatus !== OFFER_STATUS.REFUSED
  );
};

const selectableRows = computed(() => {
  return visibleRows.value.filter((row) => isDealSelectable(row.original));
});

const selectedDealKeys = computed(() => {
  return selectedDeals.value.map((deal) => getDealKey(deal));
});

const selectedCount = computed(() => selectedDeals.value.length);
const displayedSelectedCount = computed(() => {
  // While the scope modal is open the select-all is not yet confirmed, so the
  // counter must reflect the selection as it was before the header click.
  if (isScopeSelectionPending.value) {
    return selectionSnapshotBeforeSelectAll.value.length;
  }

  if (isAllAcceptedDealsSelected.value && acceptedDealCount.value !== null) {
    return acceptedDealCount.value;
  }

  return selectedCount.value;
});
const hasMultipleSelectedDeals = computed(() => displayedSelectedCount.value > 1);
const selectedRevokeModalTitle = computed(() => (
  hasMultipleSelectedDeals.value
    ? 'Tekliflerden vazgeçmek istediğinize emin misiniz?'
    : 'Tekliften vazgeçmek istediğinize emin misiniz?'
));
const selectedRevokePositiveActionText = computed(() => (
  hasMultipleSelectedDeals.value ? 'Tekliflerden Vazgeç' : 'Tekliften Vazgeç'
));

const isAllCurrentPageSelected = computed(() => {
  return (
    selectableRows.value.length > 0 &&
    selectableRows.value.every((row) =>
      selectedDealKeys.value.includes(getDealKey(row.original)),
    )
  );
});

const isSomeCurrentPageSelected = computed(() => {
  const selectedCurrentPageCount = selectableRows.value.filter((row) =>
    selectedDealKeys.value.includes(getDealKey(row.original)),
  ).length;

  return (
    selectedCurrentPageCount > 0 &&
    selectedCurrentPageCount < selectableRows.value.length
  );
});

const isRowSelected = (deal) => {
  return selectedDealKeys.value.includes(getDealKey(deal));
};

const addSelectedDeal = (deal) => {
  if (isRowSelected(deal)) return;
  selectedDeals.value = [...selectedDeals.value, deal];
};

const removeSelectedDeal = (deal) => {
  selectedDeals.value = selectedDeals.value.filter(
    (selectedDeal) => getDealKey(selectedDeal) !== getDealKey(deal),
  );
};

const handleSelectAllCurrentPage = () => {
  if (selectableRows.value.length === 0) return;

  hasResolvedRevokeScope.value = false;
  isAllAcceptedDealsSelected.value = false;
  manualSelectionChangedAfterSelectAll.value = false;

  if (isAllCurrentPageSelected.value) {
    const currentPageKeys = selectableRows.value.map((row) => getDealKey(row.original));
    selectedDeals.value = selectedDeals.value.filter(
      (deal) => !currentPageKeys.includes(getDealKey(deal)),
    );
    wasAllSelectedFromHeader.value = false;
  } else {
    selectionSnapshotBeforeSelectAll.value = [...selectedDeals.value];
    selectableRows.value.forEach((row) => addSelectedDeal(row.original));
    wasAllSelectedFromHeader.value = true;
    openRevokeModal();
  }
};

const handleToggleDeal = (deal) => {
  if (!isDealSelectable(deal)) return;

  hasResolvedRevokeScope.value = false;
  isAllAcceptedDealsSelected.value = false;

  if (isRowSelected(deal)) {
    removeSelectedDeal(deal);
  } else {
    addSelectedDeal(deal);
  }

  if (wasAllSelectedFromHeader.value) {
    manualSelectionChangedAfterSelectAll.value = true;
  }
};

const selectionColumn = {
  id: 'select-col',
  header: () =>
    h(Checkbox, {
      id: 'select-all-product-deals',
      className: 'deal-header-checkbox',
      checked: isAllCurrentPageSelected.value,
      indeterminate: isSomeCurrentPageSelected.value,
      checkBoxDisabled: selectableRows.value.length === 0,
      onClickFn: handleSelectAllCurrentPage,
    }),
  cell: ({ row }) =>
    h(
      'div',
      { class: 'deal-selection-cell' },
      h(Checkbox, {
        id: `product-deal-${getDealKey(row.original)}`,
        className: 'deal-row-checkbox',
        checked: isRowSelected(row.original),
        checkBoxDisabled: !isDealSelectable(row.original),
        onClickFn: () => handleToggleDeal(row.original),
      }),
    ),
  size: 48,
};

const tableColumns = computed(() => [selectionColumn, ...props.columns]);

dealsTable.value = useVueTable({
  get data() {
    return props.data;
  },
  get pageCount() {
    return props.pageCount;
  },
  get columns() { 
    return tableColumns.value;
  },
  getCoreRowModel: getCoreRowModel(),
  manualPagination: true,
  onPaginationChange: (updater) => {
    if (typeof updater === "function") {
      setPagination(
        updater({
          pageIndex: props.pagination.page,
          pageSize: props.pagination.pageSize,
        })
      );
    } else {
      setPagination(updater);
    }
  },
  get rowCount() {
    return props.data?.length ?? 0;
  },
  state: {
    get pagination() {
      return props.pagination;
    },
  },
  meta: {
    refresh: () => {
      const currentState = dealsTable.value.getState().pagination;
      emit("onPageChanged", {
        page: currentState.page,
        pageSize: currentState.pageSize,
        refreshKey: Date.now()
      });
    },
  }
});

const isRowDisabled = (row) => {
  return row.original.dealType === DEAL_TYPES.BO_DELETED_DEAL
}

const isRowHighLighted = (row) => {
  return row.original.dealType === DEAL_TYPES.NEW_DEAL
}

const getHeaderWidthStyle = (header) => {
  const windowWidth = tableDealRef.value.offsetWidth;
  const percentage = Math.round((header.getSize() / windowWidth) * 100);
  return { width: `${percentage}%` };
};

const setPagination = ({ pageIndex, pageSize }) => {
  const updatedPagination = { ...props.pagination, page: pageIndex, pageSize };
  emit("onPageChanged", updatedPagination);
};

const clickHandler = (pageNum) => {
  dealsTable.value.setPageIndex(pageNum);
};

const getPageSizeFromConfig = () => {
  if (!pageConfig || typeof pageConfig !== "string") {
    return defaultPageSizes;
  }
  const pageConfigArray = pageConfig.split(";").filter(Boolean);
  const newPageSizes = {};

  pageConfigArray.forEach((size) => {
    newPageSizes[size] = `${size} Ürün`;
  });
  return { ...defaultPageSizes, ...newPageSizes };
};

const onSimpleSelectChange = (newSize) => {
  dealsTable.value.setPageSize(newSize);
};

const currentPageSelectableCount = computed(() => selectableRows.value.length);
const selectedDealsScopeOptionText = computed(
  () =>
    `Sadece bu sayfadaki komisyon teklifleri (${currentPageSelectableCount.value} teklif)`,
);

const shouldAskRevokeScope = computed(() => {
  return (
    !hasResolvedRevokeScope.value &&
    !isAllAcceptedDealsSelected.value &&
    wasAllSelectedFromHeader.value &&
    isAllCurrentPageSelected.value &&
    !manualSelectionChangedAfterSelectAll.value
  );
});

const buildSelectedRevokePayload = () => {
  return selectedDeals.value.map((deal) => ({
    dealId: deal.dealDto.dealId,
    groupId: deal.dealDto.groupId,
  }));
};

const resolveAcceptedDealCount = (response) => {
  const responseData = response?.data;
  if (typeof responseData === 'number') return responseData;
  if (typeof responseData?.count === 'number') return responseData.count;
  if (typeof responseData?.acceptedCount === 'number') return responseData.acceptedCount;
  if (typeof responseData?.acceptedDealCount === 'number') {
    return responseData.acceptedDealCount;
  }

  return null;
};

const refreshTable = () => {
  dealsTable.value.options?.meta.refresh();
};

const clearSelection = () => {
  selectedDeals.value = [];
  wasAllSelectedFromHeader.value = false;
  manualSelectionChangedAfterSelectAll.value = false;
  hasResolvedRevokeScope.value = false;
  isAllAcceptedDealsSelected.value = false;
  acceptedDealCount.value = null;
  isScopeSelectionPending.value = false;
  selectionSnapshotBeforeSelectAll.value = [];
};

const showSuccessToaster = (revokedCount) => {
  store.commit("common/SET_TOASTER_DATA", {
    type: 'success',
    title: '',
    description: `Seçtiğiniz ${revokedCount} tekliften vazgeçtiniz.`,
  });
};

const showSelectionToaster = (count) => {
  store.commit("common/SET_TOASTER_DATA", {
    type: 'success',
    title: '',
    description: `${count} teklif seçildi.`,
  });
};

const showErrorToaster = (error) => {
  store.commit("common/SET_TOASTER_DATA", {
    type: 'info',
    description: error?.data?.errors?.[0]?.message || 'Teknik bir hata oluştu.',
  });
};

const openRevokeModal = async () => {
  if (displayedSelectedCount.value === 0) return;

  if (!shouldAskRevokeScope.value) {
    selectedRevokeModalRef.value?.openModal();
    return;
  }

  isScopeSelectionPending.value = true;
  allRevokeScope.value = 'CURRENT_PAGE';
  try {
    const response = await getAcceptedDealCount();
    acceptedDealCount.value = resolveAcceptedDealCount(response);
  } catch {
    acceptedDealCount.value = null;
  }

  allRevokeModalRef.value?.openModal();
};

let refreshTimeoutId = null;
let refreshCallback = null;
const scheduleTableRefresh = (delayMs = 3000, callback = null) => {
  if (refreshTimeoutId) clearTimeout(refreshTimeoutId);
  if (callback) refreshCallback = callback;

  isTableRefreshing.value = true;

  refreshTimeoutId = setTimeout(() => {
    refreshTimeoutId = null;
    refreshTable();
  }, delayMs);
};

const handleSelectedRevoke = async () => {
  isRevokeLoading.value = true;
  const revokedCount = displayedSelectedCount.value;
  try {
    if (isAllAcceptedDealsSelected.value) {
      await revokeAllDeals();
    } else {
      await revokeSelectedDeals(buildSelectedRevokePayload());
    }
    selectedRevokeModalRef.value?.hiddenModal();
    clearSelection();
    scheduleTableRefresh(3000, () => showSuccessToaster(revokedCount));
  } catch (error) {
    selectedRevokeModalRef.value?.hiddenModal();
    showErrorToaster(error);
  } finally {
    isRevokeLoading.value = false;
  }
};

const handleScopedRevoke = () => {
  hasResolvedRevokeScope.value = true;
  isAllAcceptedDealsSelected.value = allRevokeScope.value === 'ALL';
  // Confirm the provisional selection before closing so the dismiss handler
  // (fired by hiddenModal -> modalClosed) does not revert it.
  isScopeSelectionPending.value = false;
  selectionSnapshotBeforeSelectAll.value = [];
  allRevokeModalRef.value?.hiddenModal();
  showSelectionToaster(displayedSelectedCount.value);
};

// Runs on every dismiss path of the scope modal (Geri Dön button, X icon,
// outside click). If the scope was not confirmed, revert the provisional
// select-all so the header checkbox clears and the counter does not stick.
const handleScopeModalDismiss = () => {
  if (!isScopeSelectionPending.value) return;

  selectedDeals.value = [...selectionSnapshotBeforeSelectAll.value];
  selectionSnapshotBeforeSelectAll.value = [];
  wasAllSelectedFromHeader.value = false;
  manualSelectionChangedAfterSelectAll.value = false;
  hasResolvedRevokeScope.value = false;
  isAllAcceptedDealsSelected.value = false;
  isScopeSelectionPending.value = false;
};

watch(
  () => store.state.common.dealTableRefreshKey,
  (newVal) => {
    if (newVal) {
      dealsTable.value.options?.meta.refresh();
    }
  }
);

watch(
  () => props.data,
  (newData) => {
    if (selectedDeals.value.length === 0) return;
    const newDataMap = new Map(newData.map((deal) => [getDealKey(deal), deal]));
    selectedDeals.value = selectedDeals.value.filter((deal) => {
      const freshDeal = newDataMap.get(getDealKey(deal));
      if (freshDeal === undefined) return true;
      return isDealSelectable(freshDeal);
    });
  }
);

watch(
  () => props.isLoading,
  (loading, wasLoading) => {
    if (wasLoading && !loading && refreshCallback) {
      const cb = refreshCallback;
      refreshCallback = null;
      isTableRefreshing.value = false;
      cb();
      return;
    }
    if (!loading) {
      isTableRefreshing.value = false;
    }
  },
);

onUnmounted(() => {
  if (refreshTimeoutId) clearTimeout(refreshTimeoutId);
  refreshCallback = null;
});
</script>

<template>
  <div ref="tableDealRef" class="p-2 advanced-table-wrapper">
    <div class="table-top">
      <div class="table-top-header">
        <p class="table-top-title">Güncel Tekliflerimiz</p>
        <p class="table-top-text">
          Ürünlerinizin fiyatını düşürün, daha avantajlı komisyondan faydalanın.<br>
          Komisyon teklif işlemlerini aşağıdaki tablodan ya da Excel ile toplu olarak yapabilirsiniz.
        </p>
      </div>
      <AppButton
        secondary
        sm
        title="Toplu Teklif Yönetimine Git"
        class="table-top-btn"
        @click="handleGoBulkDealManagement"
      >
      </AppButton>
    </div>
    <div class="filters-wrapper">
      <AppButton
        :secondary-magenta="activeFilter === 'ALL'"
        :secondary="activeFilter !== 'ALL'"
        sm
        title="Tüm Teklifleriniz"
        class="table-top-btn active"
        @click="handleFilterChange('ALL')"
      >
      </AppButton>
      <AppButton
        :secondary-magenta="activeFilter === 'SPECIAL'"
        :secondary="activeFilter !== 'SPECIAL'"
        sm
        title="Özel Teklifleriniz"
        class="table-top-btn active"
        @click="handleFilterChange('SPECIAL')"
      >
      </AppButton>
      <div class="pagination flex items-center gap-2">
        <Pagination
          :pageCount="dealsTable.getPageCount()"
          :pageRange="5"
          :clickHandler="clickHandler"
          :firstLastButton="true"
          containerClass="button-group"
          prevLinkClass="border left rounded p-1"
          pageLinkClass="border rounded p-1"
          nextLinkClass="border right rounded p-1"
          :marginPages="0"
          :value="dealsTable.getState().pagination.page"
        >
        </Pagination>
        <div class="simple-select-wrapper">
          <p>Her Sayfada</p>
          <SimpleSelect
            :simpleSelectItems="getPageSizeFromConfig()"
            :selectedKey="dealsTable.getState().pagination.pageSize"
            openMenuPosition="top-left"
            @btnItemClick="onSimpleSelectChange(Number($event))"
          />
        </div>
      </div>
    </div>
    <div class="table-wrapper">
      <div v-if="isLoading || isTableRefreshing" class="loading">
        <img src="../../../assets/images/loading.gif" alt="Loading" />
      </div>
      <div v-else-if="visibleRows.length === 0" class="empty-table">
        <img
          src="../../../assets/images/empty-search.png"
          alt="Ürün bulunamadı"
        />
        <span class="title"
          >Bu tarih aralığında komisyon teklifi bulunmuyor.</span
        >
      </div>
      <table class="advanced-table" v-else>
        <thead>
          <tr
            v-for="headerGroup in dealsTable.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :colSpan="header.colSpan"
              :style="[getHeaderWidthStyle(header)]"
            >
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in visibleRows" :key="row.id" :class="{ 'disabled': isRowDisabled(row), 'highlighted': isRowHighLighted(row) }">
            <td v-for="cell in row.getVisibleCells()" :key="cell.id">
              <FlexRender
                :render="cell.column.columnDef.cell"
                :props="cell.getContext()"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="bulk-revoke-bar">
      <span v-if="displayedSelectedCount > 0" class="selected-count">
        {{ displayedSelectedCount }} Teklif Seçildi
      </span>
      <AppButton
        primary
        md
        title="Tekliflerden Vazgeç"
        :disabled="displayedSelectedCount === 0 || isRevokeLoading"
        class="bulk-revoke-btn"
        @click="openRevokeModal"
      />
    </div>
    <Modal
      ref="selectedRevokeModalRef"
      class="bulk-revoke-modal bulk-revoke-confirm-modal"
      :title="selectedRevokeModalTitle"
      type="warning"
      :showIcon="true"
      :withCloser="true"
      :modalStyle="{ width: '460px' }"
      negativeActionText="Geri Dön"
      :positiveActionText="selectedRevokePositiveActionText"
      :isPositiveActionButtonDisabled="isRevokeLoading"
      :isPositiveActionPending="isRevokeLoading"
      @onPositiveActionClick="handleSelectedRevoke"
      @onNegativeActionClick="selectedRevokeModalRef.hiddenModal()"
    >
      <template v-slot:body>
        <div class="bulk-revoke-modal-body">
          <div class="accept-body">
            <p v-if="hasMultipleSelectedDeals">
              Komisyon teklifleri,
              <span class="accept-body__bold">ürünlerin tüm varyantları</span>
              için
              <br />
              iptal edilecek.
            </p>
            <p v-else>
              Komisyon teklifi,
              <span class="accept-body__bold">ürünün tüm varyantları</span>
              için
              <br />
              iptal edilecek.
            </p>
            <div class="description">
              <Icons name="FilledInfoCircle" color="#4FA4E5" />
              <span>Geçerlilik tarihleri süresince bu teklife tekrar katılabilirsiniz.</span>
            </div>
          </div>
        </div>
      </template>
    </Modal>
    <Modal
      ref="allRevokeModalRef"
      class="bulk-revoke-modal bulk-revoke-scope-modal"
      title="Vazgeçmek istediğiniz teklifler için lütfen bir seçim yapın"
      :showIcon="false"
      :withCloser="true"
      :modalStyle="{ width: '460px' }"
      negativeActionText="Geri Dön"
      positiveActionText="Seç"
      :isPositiveActionButtonDisabled="isRevokeLoading"
      :isPositiveActionPending="isRevokeLoading"
      @onPositiveActionClick="handleScopedRevoke"
      @onNegativeActionClick="allRevokeModalRef.hiddenModal()"
      @modalClosed="handleScopeModalDismiss"
    >
      <template v-slot:body>
        <div class="bulk-revoke-scope">
          <div class="bulk-revoke-scope__options">
            <RadioButton
              :key="`current-page-${allRevokeScope}`"
              name="bulk-revoke-scope"
              class="scope-option"
              :preSelected="allRevokeScope === 'CURRENT_PAGE'"
              :isInputFull="true"
              @select="allRevokeScope = 'CURRENT_PAGE'"
            >
              <span class="scope-option__label">
                {{ selectedDealsScopeOptionText }}
              </span>
            </RadioButton>
            <RadioButton
              :key="`all-${allRevokeScope}`"
              name="bulk-revoke-scope"
              class="scope-option"
              :preSelected="allRevokeScope === 'ALL'"
              :isInputFull="true"
              @select="allRevokeScope = 'ALL'"
            >
              <span class="scope-option__label">
                Tüm sayfalardaki komisyon teklifleri
                <template v-if="acceptedDealCount !== null">
                  ({{ acceptedDealCount }} teklif)
                </template>
              </span>
            </RadioButton>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
@import "@/assets/styles/utilities";

.advanced-table-wrapper {
  display: flex;
  gap: 16px;
  flex-direction: column;

  .table-top {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .table-top-header {
      .table-top-title {
        color: $colorN80;
        font-size: 16px;
        font-weight: 700;
        line-height: 20px;
        letter-spacing: -0.05px;
        margin-bottom: 4px;
      }
  
      .table-top-text {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: -0.05px;
        color: $colorN80;
      }
    }

    &-btn {
      max-width: fit-content;
    }
  }

  .filters-wrapper {
    display: flex;
    flex-direction: row;
    gap: 12px;
    justify-content: center;
    align-items: center;

    .table-top-btn {
      &.secondary {
        border: 0.5px solid $colorN30 !important;
        color: $colorN80; 
      }
      &.secondary-magenta {
        border: 1px solid $colorM50 !important; 
      }
    }

    hr.line {
      width: 1px;
      height: 32px;
      background-color: $colorN40;
      margin: 0 !important;
    }

    .dropdownSelectable-btn.sm {
      padding-top: 7px !important;
      padding-bottom: 7px !important;
    }
  }

  .loading {
    height: 128px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .empty-table {
    gap: 24px;
    display: flex;
    padding: 64px 0;
    align-self: stretch;
    align-items: center;
    flex-direction: column;
    border-radius: 8px;

    img {
      max-width: 120px;
    }

    .title {
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }
  }
}

.table-wrapper {
  width: 100%;
  position: relative;
  border: 1px solid $colorN50;
  border-radius: 8px;
  border-spacing: 0;
  border-collapse: separate;
  padding: 0;
  overflow: hidden;

  .empty-table {
    display: flex;
    padding: 64px 0;
    align-items: center;
    flex-direction: column;

    img {
      max-width: 120px;
      margin-bottom: 24px;
    }

    > span {
      display: block;
      font-size: 16px;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;

      &:first-of-type {
        font-weight: 700;
        margin-bottom: 12px;
      }

      &:last-of-type {
        font-weight: 600;
        margin-bottom: 16px;
      }
    }

    button {
      width: 193px;
      gap: 4px;

      img {
        margin: auto 0;
      }
    }
  }
}

.actions-row {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.advanced-table {
  width: 100%;
  border-radius: 8px;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    background: $colorD10;
    color: $colorN80;

    tr {
      height: 32px;

      th {
        padding: 6px 12px;
        text-align: left;
        vertical-align: middle;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        color: $colorN80;
        background-color: $colorBe20;
        border-bottom: 2px solid $colorN30;

        &:not(:last-child) {
          border-right: 1px solid $colorN30;
        }

        &:first-child {
          padding: 6px 8px;
          text-align: center;
        }
      }
    }
  }

  tbody {
    tr {
      td {
        background-color: $colorN00;
        height: 90px;
        vertical-align: middle;

        > {
          div {
            margin: auto;
          }

          label {
            display: block;
            margin: 0 8px;
          }

          .product-info {
            width: 256px;
            padding: 0 12px;
          }

          .popover-wrapper {
            padding: 0 16px;
          }

          .checkbox {
            display: flex;
            justify-content: center;
          }
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN30;
        }

        &:first-child {
          text-align: center;
        }
      }

      &.disabled {
        td {
          background-color: $colorD05;
        }
      }

      &.highlighted {
        td {
          background-color: $colorB05;
        }
      }

      &:not(:last-child) {
        td {
          border-bottom: 2px solid $colorN30;
        }
      }

      &:last-child {
        td :deep(.tooltip-text) {
          top: 60px !important;
        }
      }
    }
  }
}

.deal-selection-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.deal-header-checkbox),
:deep(.deal-row-checkbox) {
  display: flex;
  align-items: center;
  justify-content: center;

  .checkbox-content {
    float: none;
    border-color: $colorN50;
  }
}

:deep(.deal-header-checkbox.disabled),
:deep(.deal-row-checkbox.disabled) {
  opacity: 1;

  .checkbox-content {
    border-color: $colorN30;
    background-color: $colorN20;

    input {
      cursor: not-allowed;
    }
  }

  .mark {
    background-color: $colorN20;
  }
}

.bulk-revoke-bar {
  position: sticky;
  bottom: 0;
  z-index: 5;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
  min-height: 72px;
  padding: 16px 0;
  margin-bottom: -24px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    z-index: -1;
    width: 100vw;
    background-color: $colorN00;
    border-top: 1px solid $colorN30;
    box-shadow: 0 -4px 12px rgba(28, 28, 30, 0.08);
    transform: translateX(-50%);
  }

  .selected-count {
    margin: 0;
    color: $colorN70;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: -0.05px;
    white-space: nowrap;
  }

  .bulk-revoke-btn {
    max-width: fit-content;
  }
}

.bulk-revoke-modal-body {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  text-align: center;

  .accept-body {
    width: 100%;
    display: flex;
    flex-direction: column;

    &__bold {
      font-weight: 700;
    }

    p {
      margin: 0 0 18px;
      color: $colorN80;
      font-weight: 400;
      line-height: 24px;
    }

    .description {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      margin-bottom: 16px;
      padding: 16px;
      border-radius: 6px;
      background-color: $colorB05;
      color: $colorN80;
      font-size: 13px;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: -0.05px;
      text-align: left;

      .icon {
        flex-shrink: 0;
      }

      span {
        white-space: nowrap;
      }
    }
  }
}

.bulk-revoke-scope {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;

  &__options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .scope-option {
    width: fit-content;
    color: $colorN80;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.05px;

    &__label {
      min-width: 0;
    }
  }

  .scope-option:deep(input:checked + .radioButton) {
    border-color: $colorN80 !important;
  }

  .scope-option:deep(input:checked + .radioButton .radioButton-inner) {
    background-color: $colorN80 !important;
  }
}

// Figma (node 5118:26969): the popup is 460x264 with a flat 20px padding and a
// 16px vertical rhythm between the close row, title, options and buttons. The
// title is left-aligned and full width (component default is 343px, centered)
// and the action buttons split the row equally.
.bulk-revoke-scope-modal {
  :deep(.modal-container) {
    padding: 20px !important;
  }

  :deep(.modal-close-icon) {
    top: 20px !important;
    right: 20px !important;
  }

  :deep(.modal-body) {
    gap: 16px;
    padding: 0;
    margin-top: 16px;
  }

  :deep(.title) {
    width: 100% !important;
    margin: 0 !important;
    text-align: left !important;
  }

  :deep(.modal-footer .button) {
    flex: 1 0 0;
  }
}

.pagination {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;

  .simple-select-wrapper {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;

    p {
      color: $colorN80;
      font-size: 12px;
      line-height: 16px;
      white-space: nowrap;
    }
  }
}

.buttons {
  display: flex;
  gap: 12px;
}
</style>
